<template>
  <!--
    AB#23060 (Zendesk#10462) — set or clear the deadline we gave the factory.

    Internal only: it is never sent to the merchant or the factory, and saving writes an
    activity-log row, not a timeline entry. The factory's own planned dispatch date (from
    its report) is shown for reference; it is kept current by the reports, not here.
  -->
  <Drawer
    form
    title="Factory deadline"
    :subtitle="`Order #${orderNo} · the date we gave the factory`"
    width="440px"
    data-test="factory-deadline-drawer"
    @close="emit('close')"
  >
    <div class="fd-field">
      <label class="fd-label" for="factory-deadline-date">Deadline we gave the factory</label>
      <Datepicker
        id="factory-deadline-date"
        v-model="date"
        format="dd/MM/yyyy"
        placeholder="Choose a date"
        data-test="factory-deadline-date"
      />
      <p v-if="reportedText" class="fd-help" data-test="factory-deadline-reported">
        {{ reportedText }}
      </p>
      <p class="fd-help">
        Internal only — never shown to the merchant or the factory. The dashboard's Factory deadline
        column shows this date, or the factory's own planned dispatch date when none is set. Clear
        it to fall back to the factory's date.
      </p>
      <p v-if="error" class="fd-error" role="status" data-test="factory-deadline-error">
        {{ error }}
      </p>
    </div>

    <template #footer>
      <button
        v-if="initial"
        type="button"
        class="dash-btn fd-clear"
        data-test="factory-deadline-clear"
        :disabled="saving"
        @click="save(null)"
      >
        Clear date
      </button>
      <button
        type="button"
        class="dash-btn"
        data-test="factory-deadline-cancel"
        @click="emit('close')"
      >
        Cancel
      </button>
      <button
        type="button"
        class="dash-btn dash-btn--primary"
        data-test="factory-deadline-save"
        :disabled="saving || !date || !changed"
        @click="save(toYmd(date))"
      >
        {{ saving ? 'Saving…' : 'Save' }}
      </button>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import Datepicker from '@/components/Shared/Datepicker.vue';
import Drawer from '@/components/Shared/Drawer.vue';
import { showToast } from '@/composables/swal';
import { http, handleResponseException } from '@/interceptors/axiosRequestInterceptor';
import { toYmd } from '@/utils/inHandDate';
import { reportingFactoryLabel } from '@/utils/factoryDeadline';
import { formatDate } from '@/composables';

const props = defineProps<{
  orderItemId: number;
  orderNo: string | number;
  /** The saved internal deadline (Y-m-d), or null. */
  initial: string | null;
  /** The factory's own planned dispatch date (Y-m-d), for reference. */
  reported?: string | null;
  reportedKey?: string | null;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  /** Saved (or cleared): the new value, Y-m-d or null. */
  (e: 'saved', value: string | null): void;
}>();

const fromYmd = (v: string | null | undefined): Date | null =>
  v ? new Date(v.slice(0, 10) + 'T00:00:00') : null;

const date = ref<Date | null>(fromYmd(props.initial));
const saving = ref(false);
const error = ref<string | null>(null);

const changed = computed(() => toYmd(date.value) !== (props.initial ?? null));
const reportedText = computed(() =>
  props.reported
    ? `${reportingFactoryLabel(props.reportedKey)}'s report says it plans to dispatch on ${formatDate(props.reported)}.`
    : ''
);

const save = async (value: string | null) => {
  saving.value = true;
  error.value = null;
  try {
    const { data } = await http.post(`customer-orders/${props.orderItemId}/factory-deadline`, {
      factory_deadline_on: value,
    });
    const saved = (data?.factory_deadline_on ?? value) as string | null;
    showToast(saved === null ? 'Factory deadline cleared.' : 'Factory deadline saved.', 'success');
    emit('saved', saved);
  } catch (e) {
    handleResponseException(e);
    error.value = 'The factory deadline could not be saved. Try again.';
  } finally {
    saving.value = false;
  }
};
</script>

<style lang="scss" scoped>
.fd-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.fd-label {
  font-size: 12.5px;
  font-weight: 700;
  color: var(--ink);
}
.fd-help {
  margin: 0;
  font-size: 11.5px;
  line-height: 1.45;
  color: var(--ink-mute);
}
.fd-error {
  margin: 0;
  font-size: 12px;
  color: var(--danger);
}
.fd-clear {
  margin-right: auto;
  border-color: transparent;
  color: var(--ink-mute);
}
</style>
